import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getCollection, updateCollectionItem } from '../utils/storage'
import styles from './CollectionAdd.module.css'

export default function CollectionEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    artist: '',
    condition: '',
    memo: '',
    isTradeAvailable: false,
    wantedInput: '',
  })
  const [wantedItems, setWantedItems] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      const item = getCollection().find(i => i.id === id)
      if (item) {
        setFormData({
          title: item.title,
          category: item.category,
          artist: item.artist || '',
          condition: item.condition,
          memo: item.memo || '',
          isTradeAvailable: item.isTradeAvailable,
          wantedInput: '',
        })
        setWantedItems(item.wantedItems || [])
      }
      setLoading(false)
    }
  }, [id])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return
    updateCollectionItem(id, {
      title: formData.title,
      category: formData.category,
      artist: formData.artist || undefined,
      condition: formData.condition as 'new' | 'like_new' | 'good' | 'fair' | 'poor',
      memo: formData.memo || undefined,
      isTradeAvailable: formData.isTradeAvailable,
      wantedItems,
    })
    navigate(`/collection/${id}`)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    })
  }

  const handleAddWanted = () => {
    if (formData.wantedInput.trim()) {
      setWantedItems([...wantedItems, formData.wantedInput.trim()])
      setFormData({ ...formData, wantedInput: '' })
    }
  }

  const handleRemoveWanted = (index: number) => {
    setWantedItems(wantedItems.filter((_, i) => i !== index))
  }

  if (loading) {
    return <div className={styles.container}>読み込み中...</div>
  }

  if (!formData.title) {
    return (
      <div className={styles.container}>
        <p>アイテムが見つかりません</p>
        <Link to="/collection">コレクションに戻る</Link>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h1>コレクションを編集</h1>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.field}>
          <label htmlFor="title">アイテム名</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="例: ○○ アクリルスタンド"
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="category">カテゴリ</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          >
            <option value="">選択してください</option>
            <option value="acrylic">アクリルスタンド</option>
            <option value="penlight">ペンライト</option>
            <option value="photo">写真・ブロマイド</option>
            <option value="towel">タオル</option>
            <option value="tshirt">Tシャツ</option>
            <option value="other">その他</option>
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="artist">アーティスト名</label>
          <input
            type="text"
            id="artist"
            name="artist"
            value={formData.artist}
            onChange={handleChange}
            placeholder="例: ○○グループ / △△さん"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="condition">状態</label>
          <select
            id="condition"
            name="condition"
            value={formData.condition}
            onChange={handleChange}
            required
          >
            <option value="">選択してください</option>
            <option value="new">新品・未使用</option>
            <option value="like_new">未使用に近い</option>
            <option value="good">目立った傷や汚れなし</option>
            <option value="fair">やや傷や汚れあり</option>
            <option value="poor">傷や汚れあり</option>
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="memo">メモ</label>
          <textarea
            id="memo"
            name="memo"
            rows={3}
            value={formData.memo}
            onChange={handleChange}
            placeholder="購入場所、日付など"
          />
        </div>

        <div className={styles.checkboxField}>
          <label>
            <input
              type="checkbox"
              name="isTradeAvailable"
              checked={formData.isTradeAvailable}
              onChange={handleChange}
            />
            交換可能にする
          </label>
        </div>

        {formData.isTradeAvailable && (
          <div className={styles.field}>
            <label>交換希望（これと交換したい）</label>
            <div className={styles.wantedInput}>
              <input
                type="text"
                name="wantedInput"
                value={formData.wantedInput}
                onChange={handleChange}
                placeholder="例: ○○のアクスタ"
              />
              <button type="button" onClick={handleAddWanted} className={styles.addWantedButton}>
                追加
              </button>
            </div>
            {wantedItems.length > 0 && (
              <ul className={styles.wantedList}>
                {wantedItems.map((item, index) => (
                  <li key={index}>
                    {item}
                    <button type="button" onClick={() => handleRemoveWanted(index)}>×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button type="submit" className={styles.submitButton}>
          保存する
        </button>
      </form>
    </div>
  )
}
